import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../auth';
import { UserMenu } from '../../components/UserMenu';
import type { Recording } from '@modelcast/types';
import { Wordmark } from '../../components/Wordmark';

// Extended recording with playback URL
interface RecordingWithPlayback extends Recording {
  playbackUrl?: string | null;
}

export function RecordingReviewPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [recordings, setRecordings] = useState<RecordingWithPlayback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecording, setSelectedRecording] = useState<RecordingWithPlayback | null>(null);
  const [loadingPlayback, setLoadingPlayback] = useState(false);
  const [downloadingRecordingId, setDownloadingRecordingId] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<string | null>(null);
  const [recordingPrices, setRecordingPrices] = useState<Record<string, string>>({});
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    fetchPendingRecordings();
  }, []);

  const fetchPendingRecordings = async () => {
    try {
      const token = await getToken();
      const response = await fetch('/api/recordings/pending-review', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load recordings');
      }

      const data = await response.json();
      const nextRecordings = data.recordings || [];
      setRecordings(nextRecordings);
      setRecordingPrices(Object.fromEntries(
        nextRecordings.map((recording: RecordingWithPlayback) => [
          recording.id,
          recording.priceCents ? (recording.priceCents / 100).toFixed(2) : '',
        ])
      ));
    } catch (err) {
      console.error('Error fetching recordings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRecording = async (recording: RecordingWithPlayback) => {
    setSelectedRecording(recording);
    setLoadingPlayback(true);

    try {
      const token = await getToken();
      const response = await fetch(`/api/recordings/id/${recording.id}/playback`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load video');
      }

      const data = await response.json();
      setSelectedRecording({ ...recording, playbackUrl: data.playbackUrl });
    } catch (err) {
      console.error('Error loading playback URL:', err);
      alert('Failed to load video preview');
    } finally {
      setLoadingPlayback(false);
    }
  };

  const handleRelease = async (recordingId: string, action: 'release' | 'reject') => {
    setReleasing(recordingId);
    try {
      const token = await getToken();
      const priceCents = action === 'release'
        ? Math.max(0, Math.round((Number(recordingPrices[recordingId]) || 0) * 100))
        : null;
      const response = await fetch(`/api/recordings/id/${recordingId}/release`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, priceCents, currency: 'USD' }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} recording`);
      }

      // Remove from list
      setRecordings(prev => prev.filter(r => r.id !== recordingId));

      // Clear selection if it was the released recording
      if (selectedRecording?.id === recordingId) {
        setSelectedRecording(null);
      }
    } catch (err) {
      console.error(`Error ${action}ing recording:`, err);
      alert(`Failed to ${action} recording`);
    } finally {
      setReleasing(null);
    }
  };

  const handleDownload = async (recordingId: string) => {
    setDownloadingRecordingId(recordingId);
    try {
      const token = await getToken();
      const response = await fetch(`/api/recordings/id/${recordingId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Recording not available for download');
      }

      const data = await response.json();
      if (!data.downloadUrl) {
        throw new Error('Recording not available for download');
      }

      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = data.filename || 'session-recording.mp4';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading recording:', err);
      alert(err instanceof Error ? err.message : 'Failed to download recording');
    } finally {
      setDownloadingRecordingId(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Unknown date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-text-dim">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="bg-panel border border-line rounded-xl p-6 max-w-md text-center">
          <h2 className="text-lg font-medium text-warn mb-2">Error</h2>
          <p className="text-text-dim mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      {/* Header */}
      <header className="border-b border-line chrome-gradient">
        <div className="page-container h-14 flex items-center gap-5">
          <Wordmark />
          <div className="w-px h-5 bg-line" />
          <span className="text-sm text-text-dim font-mono">Recording Review</span>
          <div className="flex-1" />
          <Link to="/profile" className="text-sm text-text-dim hover:text-text transition-colors">
            Profile
          </Link>
          <UserMenu />
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-bg">
        <div className="page-container py-14">
          <div className="page-header">
            <div className="page-eyebrow">Your recordings</div>
            <h1 className="page-heading">
              Review & Release
            </h1>
            <p className="text-base text-text-dim leading-relaxed mt-3.5">
              Review recordings from your sessions and release them to artists when you're satisfied.
            </p>
          </div>

          {recordings.length === 0 ? (
            <div className="bg-panel border border-line rounded-xl p-10 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-panel2 flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-text-dim">
                  <path d="M23 7l-7 5 7 5V7z" />
                  <rect x="1" y="5" width="15" height="14" rx="2" />
                </svg>
              </div>
              <h3 className="font-display text-xl font-medium text-text mb-2">
                No recordings to review
              </h3>
              <p className="text-text-dim max-w-md mx-auto">
                When you have session recordings pending your approval, they'll appear here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
              {/* Recording List */}
              <div className="flex flex-col gap-4">
                {recordings.map((recording) => (
                  <div
                    key={recording.id}
                    className={`bg-panel border rounded-xl p-5 cursor-pointer transition-all ${
                      selectedRecording?.id === recording.id
                        ? 'border-accent shadow-lg'
                        : 'border-line hover:border-text/30'
                    }`}
                    onClick={() => handleSelectRecording(recording)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Thumbnail placeholder */}
                      <div className="w-24 h-16 rounded-lg bg-panel2 border border-line flex items-center justify-center flex-shrink-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-text-dim">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="text-[15px] font-medium text-text mb-1">
                          {recording.label || 'Session Recording'}
                        </div>
                        <div className="text-[13px] text-text-dim">
                          {formatDate(recording.startedAt)}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-[12px] text-text-faint">
                          <span>Duration: {formatDuration(recording.durationSeconds)}</span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 text-amber-600 rounded">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Pending
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Preview Panel */}
              <aside className="lg:sticky lg:top-6">
                <div className="bg-panel border border-line rounded-xl overflow-hidden">
                  {selectedRecording ? (
                    <>
                      {/* Video Preview */}
                      <div className="relative aspect-video bg-black">
                        {loadingPlayback ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          </div>
                        ) : selectedRecording.playbackUrl ? (
                          <video
                            ref={videoRef}
                            src={selectedRecording.playbackUrl}
                            controls
                            className="w-full h-full"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-white/60">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="p-5">
                        <div className="text-[15px] font-medium text-text mb-1">
                          {selectedRecording.label || 'Session Recording'}
                        </div>
                        <div className="text-[13px] text-text-dim mb-4">
                          {formatDate(selectedRecording.startedAt)} · {formatDuration(selectedRecording.durationSeconds)}
                        </div>

                        <p className="text-[13px] text-text-dim mb-5 leading-relaxed">
                          Review this recording, set the artist access price, and decide whether to release it.
                        </p>

                        <button
                          type="button"
                          onClick={() => handleDownload(selectedRecording.id)}
                          disabled={downloadingRecordingId === selectedRecording.id}
                          className="btn btn-sm mb-5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingRecordingId === selectedRecording.id ? 'Preparing download...' : 'Download'}
                        </button>

                        <label className="block mb-5">
                          <span className="text-sm text-text-dim">Recording price</span>
                          <div className="flex items-center mt-1">
                            <span className="h-11 px-3 inline-flex items-center border border-r-0 border-line rounded-l bg-bg-secondary text-text-dim">$</span>
                            <input
                              value={recordingPrices[selectedRecording.id] ?? ''}
                              onChange={(event) => setRecordingPrices(prev => ({
                                ...prev,
                                [selectedRecording.id]: event.target.value,
                              }))}
                              className="input h-11 rounded-l-none text-sm"
                              inputMode="decimal"
                              placeholder="0 for free"
                            />
                          </div>
                          <span className="block text-[11px] text-text-faint mt-1">
                            Leave blank or enter 0 to release this recording for free.
                          </span>
                        </label>

                        <div className="flex gap-3">
                          <button
                            onClick={() => handleRelease(selectedRecording.id, 'release')}
                            disabled={releasing === selectedRecording.id}
                            className="btn btn-accent flex-1"
                          >
                            {releasing === selectedRecording.id ? (
                              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Release
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleRelease(selectedRecording.id, 'reject')}
                            disabled={releasing === selectedRecording.id}
                            className="btn flex-1"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                            Reject
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="p-10 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-panel2 flex items-center justify-center">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-text-dim">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      </div>
                      <p className="text-text-dim text-[13px]">
                        Select a recording to preview
                      </p>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
